import React from "react";
import { Eye, RotateCcw, Search } from "lucide-react";
import { Button } from "../ui/Button";

export interface ZReportItem {
  id: number;
  date: string;
  dateObj?: Date;
  no_of_sales: number;
  total_sale_amount: string;
  products_total_amount: string;
  services_total_amount: string;
  total_tender_amount: string;
  total_quick_payments: string;
}

interface ZReportTableProps {
  reports: ZReportItem[];
  isLoading: boolean;
  indexOfFirstItem: number;
  onView: (report: ZReportItem) => void;
  onReset?: (report: ZReportItem) => void;
  resetLoading?: boolean;
}

export const ZReportTable: React.FC<ZReportTableProps> = ({
  reports,
  isLoading,
  indexOfFirstItem,
  onView,
  onReset,
  resetLoading = false,
}) => {
  return (
    <div className="flex-1 lg:overflow-auto relative custom-scrollbar rounded-xl border-0 lg:border border-gray-200 min-w-0">
      <table className="w-full border-collapse min-w-[800px]">
        <thead className="sticky top-0 z-10 bg-[#885ABB] border-b border-gray-100 hidden lg:table-header-group">
          <tr className="grid gap-2 text-sm font-bold text-white tracking-wide px-6 py-4 lg:grid-cols-[minmax(2rem,2.5rem)_minmax(8rem,1.2fr)_minmax(3rem,0.8fr)_1fr_1fr_1fr_1fr_1fr_1fr]">
            <th className="text-left font-bold whitespace-nowrap min-w-0">#</th>
            <th className="text-left font-bold whitespace-nowrap min-w-0">Date</th>
            <th className="text-left font-bold whitespace-nowrap min-w-0">Sales</th>
            <th className="text-left font-bold whitespace-nowrap min-w-0">Total Net Sale</th>
            <th className="text-left font-bold whitespace-nowrap min-w-0">Product Sale Net</th>
            <th className="text-left font-bold whitespace-nowrap min-w-0">Services Sale Net</th>
            <th className="text-left font-bold whitespace-nowrap min-w-0">Tendering</th>
            <th className="text-left font-bold whitespace-nowrap min-w-0">Total Quick Payments</th>
            <th className="text-right font-bold whitespace-nowrap min-w-0">Action</th>
          </tr>
        </thead>
        <tbody className="w-full lg:divide-y lg:divide-gray-100 bg-transparent lg:bg-white flex flex-col lg:table-row-group gap-4 lg:gap-0 p-4 lg:p-0">
          {isLoading ? (
            <tr className="h-64 w-full flex items-center justify-center lg:table-row">
              <td colSpan={9} className="h-full">
                <div className="flex h-full w-full items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent"></div>
                </div>
              </td>
            </tr>
          ) : reports.length === 0 ? (
            <tr className="flex flex-col items-center justify-center h-64 text-gray-400 w-full lg:table-row">
              <td colSpan={9} className="w-full h-full p-0">
                <div className="flex flex-col items-center justify-center h-full w-full">
                  <Search className="w-12 h-12 mb-3 opacity-20" />
                  <p className="text-lg font-medium text-gray-500">
                    No reports found
                  </p>
                  <p className="text-sm">Try adjusting your search terms</p>
                </div>
              </td>
            </tr>
          ) : (
            <>
              {reports.map((report, idx) => (
                <tr
                  key={report.id}
                  className="flex flex-col lg:grid lg:grid-cols-[minmax(2rem,2.5rem)_minmax(8rem,1.2fr)_minmax(3rem,0.8fr)_1fr_1fr_1fr_1fr_1fr_1fr] gap-3 lg:gap-2 items-stretch lg:items-center px-5 py-4 lg:px-6 hover:bg-purple-50/30 transition-colors bg-white rounded-xl lg:rounded-none border border-gray-200 lg:border-0 shadow-sm lg:shadow-none group text-sm w-full"
                >
                  <td className="lg:w-10 text-gray-400 font-bold lg:font-medium flex justify-between items-center lg:block">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Record #</span>
                    <span>{indexOfFirstItem + idx + 1}</span>
                  </td>

                  <td className="lg:min-w-0 font-bold lg:font-medium text-gray-900 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0 overflow-hidden">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Date</span>
                    <span className="truncate block min-w-0">{report.date}</span>
                  </td>

                  <td className="lg:min-w-0 font-medium text-gray-700 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0 shrink-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Sales</span>
                    <span className="font-bold lg:font-normal">{report.no_of_sales}</span>
                  </td>

                  <td className="lg:min-w-0 font-bold text-[#885ABB] lg:text-gray-700 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Net Sale</span>
                    <span className="text-base lg:text-sm truncate">{report.total_sale_amount}</span>
                  </td>

                  <td className="lg:min-w-0 font-medium text-gray-600 lg:text-gray-700 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Product Sale</span>
                    <span className="truncate">{report.products_total_amount}</span>
                  </td>

                  <td className="lg:min-w-0 font-medium text-gray-600 lg:text-gray-700 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Service Sale</span>
                    <span className="truncate">{report.services_total_amount}</span>
                  </td>

                  <td className="lg:min-w-0 font-medium text-gray-600 lg:text-gray-700 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Tendering</span>
                    <span className="truncate">{report.total_tender_amount}</span>
                  </td>

                  <td className="lg:min-w-0 font-medium text-gray-600 lg:text-gray-700 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Quick Payments</span>
                    <span className="truncate">{report.total_quick_payments}</span>
                  </td>

                  <td className="lg:min-w-0 border-t border-gray-50 lg:border-0 pt-4 lg:pt-0">
                    <div className="flex justify-end lg:justify-end gap-3 w-full">
                      <Button
                        size="sm"
                        className="flex-1 lg:flex-none h-11 lg:h-9 bg-[#F3E8FF] hover:bg-[#E9D5FF] text-[#885ABB] rounded-xl lg:rounded-lg font-bold lg:font-medium shadow-sm border border-transparent flex items-center justify-center gap-2"
                        onClick={() => onView(report)}
                        title="View Report"
                      >
                        <Eye size={18} className="lg:w-4 lg:h-4" />
                        <span className="lg:hidden">View Report</span>
                      </Button>
                      {onReset && (
                        <Button
                          size="sm"
                          className="flex-1 lg:flex-none h-11 lg:h-9 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl lg:rounded-lg font-bold lg:font-medium shadow-sm border border-transparent flex items-center justify-center gap-2 disabled:opacity-50"
                          onClick={() => onReset(report)}
                          disabled={resetLoading}
                          title="Reset Report"
                        >
                          <RotateCcw size={18} className="lg:w-4 lg:h-4" />
                          <span className="lg:hidden">Reset</span>
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};
